import { MethodPackManager } from "../../MethodPackManager";
import { ScanTree } from "../../cluetheory/scans/ScanTree";
import { GenericPathMethodEditor } from "./GenericPathMethodEditor";
import { PathEditor } from "../../pathedit/PathEditor";
import { NisModal } from "../../../lib/ui/NisModal";
import { Checkbox } from "../../../lib/ui/controls/Checkbox";
import { BigNisButton } from "../widgets/BigNisButton";
import ExportStringModal from "../widgets/modals/ExportStringModal";
import { util } from "../../../lib/util/util";
import ButtonRow from "../../../lib/ui/ButtonRow";
import Properties from "../widgets/Properties";
import { NewMethodPackModal } from "./MethodPackModal";
import { AugmentedMethod } from "../../model/MethodPack";
export var MethodNormalization;
(function (MethodNormalization) {
    var normalizeFarDive = PathEditor.normalizeFarDive;
    var cleanedJSON = util.cleanedJSON;
    class Normalizer {
        async step(step) {
            return step;
        }
        async path(path) {
            return await Promise.all(path.map(async (step) => {
                return this.step(step);
            }));
        }
        async method(method) {
            switch (method.method.type) {
                case "general_path":
                    return {
                        ...method.method,
                        pre_path: !method.method.pre_path ? undefined : await this.path(method.method.pre_path),
                        main_path: !method.method.main_path ? undefined : await this.path(method.method.main_path),
                        post_path: !method.method.post_path ? undefined : await this.path(method.method.post_path),
                    };
                case "scantree":
                    const forNode = async (node) => {
                        return {
                            ...node,
                            path: await this.path(node.path),
                            children: await Promise.all(node.children.map(async (c) => ({
                                key: c.key,
                                value: await forNode(c.value)
                            })))
                        };
                    };
                    return {
                        ...method.method,
                        tree: {
                            ...method.method.tree,
                            root: await forNode(method.method.tree.root)
                        }
                    };
            }
        }
        async pack(pack) {
            return {
                ...pack,
                methods: await Promise.all(pack.methods.map(m => this.method(AugmentedMethod.create(m, pack))))
            };
        }
    }
    function update_far_dives() {
        return new class extends Normalizer {
            async step(step) {
                return await normalizeFarDive(step);
            }
        };
    }
    MethodNormalization.update_far_dives = update_far_dives;
    function update_timings() {
        return new class extends Normalizer {
            async method(method) {
                switch (method.method.type) {
                    case "general_path":
                        return {
                            ...method.method,
                            expected_time: (await GenericPathMethodEditor.getEndState(method)).tick
                        };
                    case "scantree":
                        return {
                            ...method.method,
                            expected_time: (await ScanTree.Augmentation.augment({ augment_paths: true, analyze_timing: true, path_assumptions: method.method.assumptions }, method.method.tree, method.clue.clue)).state.timing_analysis.statistics.average + 1 // Add 1 tick for digging
                        };
                }
            }
        };
    }
    MethodNormalization.update_timings = update_timings;
    function chain(...normalizers) {
        return new class extends Normalizer {
            async pack(pack) {
                return await normalizers.reduce(async (pack, normalizer) => await normalizer.pack(await pack), Promise.resolve(pack));
            }
        };
    }
    MethodNormalization.chain = chain;
    function forSettings(settings) {
        const normalizers = [];
        if (settings.update_far_dives)
            normalizers.push(update_far_dives());
        if (settings.recalculate_timings)
            normalizers.push(update_timings());
        return chain(...normalizers);
    }
    MethodNormalization.forSettings = forSettings;
    class Modal extends NisModal {
        constructor(pack) {
            super();
            this.pack = pack;
            this.settings = { recalculate_timings: true };
            this.mode = "show";
            this.title.set("Normalize Method Pack");
        }
        render() {
            super.render();
            const layout = new Properties().appendTo(this.body);
            layout.header(new Checkbox("Update Far Dives")
                .onCommit(v => this.settings.update_far_dives = v));
            layout.paragraph("Update all dives to correctly contain a flag that indicates whether it's a far dive or a precise dive.");
            layout.header(new Checkbox("Recalculate timings")
                .setValue(true)
                .onCommit(v => this.settings.recalculate_timings = v));
            layout.paragraph("Recalculate expected timings for all methods.");
            const grp = new Checkbox.Group([
                { button: new Checkbox("Show JSON"), value: "show" },
                { button: new Checkbox("Save").setEnabled(!this.pack.is_real_default), value: "save" },
                { button: new Checkbox("Save Copy"), value: "savecopy" },
            ])
                .setValue(this.mode)
                .onChange(v => this.mode = v);
            new ButtonRow().buttons(...grp.checkboxes()).appendTo(this.body);
        }
        async execute() {
            const updated = await forSettings(this.settings).pack(this.pack);
            switch (this.mode) {
                case "show":
                    new ExportStringModal(cleanedJSON(updated)).show();
                    break;
                case "save":
                    MethodPackManager.instance().updatePack(this.pack, p => p.methods = updated.methods);
                    break;
                case "savecopy":
                    new NewMethodPackModal(updated).do();
                    break;
            }
            this.remove();
        }
        getButtons() {
            return [
                new BigNisButton("Cancel", "cancel").onClick(() => this.remove()),
                new BigNisButton("Confirm", "confirm").onClick(() => this.execute())
            ];
        }
    }
    MethodNormalization.Modal = Modal;
})(MethodNormalization || (MethodNormalization = {}));
